/**
 * What the valley remembers about pushing back (M5-02).
 *
 * The **record** half of the pressure scheduler, and a leaf: it names nothing
 * but numbers, so `world.ts` can hold a `pressure` table without the rules half
 * dragging the whole tick loop in behind it. The rules — the conditions, the
 * dice, the system that runs them — are `scheduler.ts`'s, exactly as `Den` is
 * here and what a den *does* is `beasts.ts`'s.
 *
 * ## Why a governor at all
 *
 * Because the MVP must not eat its own stakes. A raid rule with no ceiling on
 * it will, over a fortnight of a world that never sleeps, kill every animal in
 * the fold long before anybody gets round to the boar — and the thing that
 * makes the boar matter is a village that still has something left to lose.
 * `perWeek` is that ceiling, and it is a hard cap rather than a soft bias
 * because a soft one is a cap you discover was not one at three in the morning.
 */
import { TICKS_PER_GAME_DAY } from './time.js';
export function createPressure() {
    return { lastAt: -1, count: 0, windowFrom: 0 };
}
/**
 * The window a `perWeek` cap is counted over: seven game days, which is seven
 * real hours.
 *
 * A *game* week rather than a real one, and that is the honest unit even though
 * the ticket says "per real week": everything else in the valley is paced in
 * game days, and a cap in real time would mean a world left running overnight
 * accumulates a raid budget nobody was there to spend.
 */
export const PRESSURE_WINDOW = TICKS_PER_GAME_DAY * 7;
/**
 * Why this rule may not fire right now, or `null`.
 *
 * Split out and exported because it is the half a balance pass reads and a test
 * asserts against: "never while the den is sated" is a sentence somebody should
 * be able to check without running a thousand game weeks.
 */
export function pressureFault(state, limits, tick) {
    if (state.lastAt >= 0 && tick - state.lastAt < limits.cooldown)
        return 'too soon';
    if (state.count >= limits.perWeek)
        return 'spent for this week';
    return null;
}
/**
 * Advance one rule's window and take its state.
 *
 * The window slides in whole weeks rather than continuously, so the budget is a
 * thing that resets on a date rather than a thing that trickles back — which is
 * both easier to reason about and much easier to write a statistical test
 * against.
 */
export function pressureFor(pressures, id, tick) {
    const state = (pressures[id] ??= createPressure());
    if (tick - state.windowFrom >= PRESSURE_WINDOW) {
        state.windowFrom = tick;
        state.count = 0;
    }
    return state;
}
//# sourceMappingURL=pressure.js.map